import React, { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Nav from "../Components/Nav";
import Footer from "../Components/Footer";
import { auth, db } from "../config";
import {
  doc,
  onSnapshot,
  addDoc,
  collection,
  getDocs,
  query,
  where,
  updateDoc,
  orderBy,
  limit,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

const Icon = ({ name, className = " ", style = {} }) => {
  const icons = {
    deposit: "fas fa-arrow-down-to-bracket",
    withdrawal: "fas fa-arrow-up-from-bracket",
    investment: "fas fa-chart-pie",
    bonus: "fas fa-gift",
    pending: "fas fa-clock",
    completed: "fas fa-circle-check",
    failed: "fas fa-circle-xmark",
    active: "fas fa-play-circle",
    search: "fas fa-magnifying-glass",
    download: "fas fa-file-export",
    filter: "fas fa-filter",
    calendar: "fas fa-calendar-days",
    refresh: "fas fa-rotate",
    mt4: "fas fa-terminal",
    wallet: "fas fa-wallet",
  };
  return <i className={`${icons[name] || "fas fa-circle"} ${className}`} style={style} />;
};

const StatusBadge = ({ status }) => {
  const styles = {
    pending: "bg-warning bg-opacity-10 text-warning border-warning",
    completed: "bg-success bg-opacity-10 text-success border-success",
    approved: "bg-success bg-opacity-10 text-success border-success",
    failed: "bg-danger bg-opacity-10 text-danger border-danger",
    processing: "bg-info bg-opacity-10 text-info border-info",
    active: "bg-success bg-opacity-10 text-success border-success",
    expired: "bg-secondary bg-opacity-10 text-secondary border-secondary",
    default: "bg-light text-dark border-light",
  };
  const labels = {
    pending: "Pending",
    completed: "Completed",
    approved: "Approved",
    failed: "Failed",
    processing: "Processing",
    active: "Active",
    expired: "Expired",
  };
  const lowerStatus = status?.toLowerCase();
  const badgeStyle = styles[lowerStatus] || styles.default;
  const label = labels[lowerStatus] || status;
  return (
    <span className={`badge border ${badgeStyle} px-3 py-2 fw-medium`}>
      <Icon name={lowerStatus} className="me-1 small" />
      {label}
    </span>
  );
};

const EmptyState = ({ icon, title, description, action }) => (
  <div className="text-center py-5">
    <div className="mb-4">
      <Icon name={icon} className="fa-3x text-muted opacity-50" />
    </div>
    <h5 className="text-muted fw-medium mb-2">{title}</h5>
    <p className="text-muted small mb-4">{description}</p>
    {action}
  </div>
);

const TableRow = ({ children }) => (
  <tr className="border-bottom border-light hover-bg-light">{children}</tr>
);

// ✅ UNIVERSAL DATE FORMATTER: Handles Firestore Timestamps, ISO strings, and JS Dates
const formatDate = (dateValue) => {
  if (!dateValue) return "—";
  try {
    if (dateValue?.seconds) {
      return new Date(dateValue.seconds * 1000).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
    return new Date(dateValue).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (e) {
    return "—";
  }
};

// ✅ UNIVERSAL DATE PARSER for sorting/filtering
const getDateValue = (item) => {
  if (item.timestamp?.seconds) return item.timestamp.seconds * 1000;
  if (item.createdAt?.seconds) return item.createdAt.seconds * 1000;
  if (item.timestamp) return new Date(item.timestamp).getTime();
  if (item.createdAt) return new Date(item.createdAt).getTime();
  return 0;
};

function UserHistory() {
  const [user, setUser] = useState(null);
  const [balance, setBalance] = useState(0);
  const [totalDeposit, setTotalDeposit] = useState(0);
  const [totalWithdrawal, setTotalWithdrawal] = useState(0);
  const [transactions, setTransactions] = useState({
    deposits: [],
    withdrawals: [],
    investments: [],
    others: [],
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("deposits");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [mt4Form, setMt4Form] = useState({
    duration: "default",
    userid: "",
    pswrd: "",
    acntype: "",
    currency: "",
    leverage: "",
    server: "",
  });
  const navigate = useNavigate();

  const stats = useMemo(() => {
    const sum = (arr, field) =>
      arr.reduce((total, item) => total + (Number(item[field]) || 0), 0);
    return {
      totalDeposits: totalDeposit, // ✅ Real-time from user doc
      totalWithdrawals: totalWithdrawal, // ✅ Real-time from user doc
      activeInvestments: transactions.investments.filter(
        (i) => i.status?.toLowerCase() === "active"
      ).length,
      totalInvested: sum(
        transactions.investments.filter(
          (i) => i.status?.toLowerCase() === "active"
        ),
        "amount"
      ),
      netBalance: balance,
    };
  }, [totalDeposit, totalWithdrawal, transactions, balance]);

  const filterTransactions = (items) => {
    let filtered = [...items];
    
    // Date filter
    if (dateFilter !== "all") {
      const now = Date.now();
      const periods = { "7d": 7, "30d": 30, "90d": 90 };
      const days = periods[dateFilter];
      const cutoff = now - days * 24 * 60 * 60 * 1000;
      filtered = filtered.filter((item) => getDateValue(item) >= cutoff);
    }
    
    // Search filter
    if (searchQuery.trim()) {
      const queryStr = searchQuery.toLowerCase();
      filtered = filtered.filter((item) =>
        Object.values(item).join(" ").toLowerCase().includes(queryStr)
      );
    }
    
    // Sort newest first using universal parser
    return filtered.sort((a, b) => getDateValue(b) - getDateValue(a));
  };

  useEffect(() => {
    let unsubUser = null;
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        try {
          const userDocRef = doc(db, "users", currentUser.uid);
          
          // ✅ Real-time listener for user data (Balance, Total Deposits, Total Withdrawals)
          unsubUser = onSnapshot(userDocRef, (docSnap) => {
            if (docSnap.exists()) {
              const data = docSnap.data();
              setBalance(data.balance || 0);
              setTotalDeposit(data.Totaldeposit || 0);
              setTotalWithdrawal(data.Totalwithdrawal || 0);
            }
          });

          const fetchCollection = async (colName) => {
            // ✅ FIX: Deposits use 'timestamp', others use 'createdAt'
            const dateField = colName === "deposits" ? "timestamp" : "createdAt";
            const q = query(
              collection(db, colName),
              where("userId", "==", currentUser.uid),
              orderBy(dateField, "desc"),
              limit(50)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
          };

          const [deposits, withdrawals, investments, others] = await Promise.all([
            fetchCollection("deposits"),
            fetchCollection("withdrawals"),
            fetchCollection("investments"),
            fetchCollection("others"),
          ]);
          
          setTransactions({ deposits, withdrawals, investments, others });
        } catch (error) {
          console.error("Error fetching transaction", error);
          toast.error("Failed to load transaction history");
        } finally {
          setLoading(false);
        }
      } else {
        navigate("/auth/login");
      }
    });
    return () => {
      unsubscribe();
      if (unsubUser) unsubUser();
    };
  }, [navigate]);

  const calcAmount = (duration) => ({ Monthly: 100, Quarterly: 250, Yearly: 900 }[duration] || 0);

  const handleMt4Submit = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Please log in to subscribe");
    const { duration, userid, pswrd, acntype, currency, leverage, server } = mt4Form;
    if (duration === "default" || !userid || !pswrd || !acntype || !currency || !leverage || !server) {
      return toast.error("All fields are required");
    }
    const amount = calcAmount(duration);
    if (!amount) return toast.error("Invalid subscription duration");
    if (amount > balance) return toast.error("Insufficient balance");
    setSubmitting(true);
    try {
      await addDoc(collection(db, "mt4Subscriptions"), {
        userId: user.uid,
        email: user.email,
        duration,
        amount,
        userid,
        pswrd,
        acntype,
        currency,
        leverage,
        server,
        createdAt: new Date(),
        status: "pending",
      });
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, { balance: balance - amount });
      setBalance((prev) => prev - amount);
      toast.success("MT4 subscription submitted!");
      setMt4Form({ duration: "default", userid: "", pswrd: "", acntype: "", currency: "", leverage: "", server: "" });
    } catch (error) {
      console.error("MT4 subscription error:", error);
      toast.error("Failed to submit subscription");
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMt4Form((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "duration" && { amount: calcAmount(value) }),
    }));
  };

  const exportToCSV = (data, filename) => {
    if (!data.length) return toast.info("No data to export");
    const headers = Object.keys(data[0]).filter((k) => !k.includes("Id"));
    const csv = [
      headers.join(","),
      ...data.map((row) =>
        headers
          .map((field) => {
            const val = row[field];
            return typeof val === "string" && val.includes(",") ? `"${val}"` : val;
          })
          .join(",")
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Exported successfully");
  };

  const tabs = [
    { id: "deposits", label: "Deposits", icon: "deposit", count: transactions.deposits.length },
    { id: "withdrawals", label: "Withdrawals", icon: "withdrawal", count: transactions.withdrawals.length },
    { id: "investments", label: "Investments", icon: "investment", count: transactions.investments.length },
    { id: "others", label: "Other", icon: "bonus", count: transactions.others.length },
  ];

  const getTableHeaders = (tab) => {
    switch (tab) {
      case "deposits": return ["Amount", "Method", "Status", "Date"];
      case "withdrawals": return ["Amount", "Fee", "Method", "Status", "Date"];
      case "investments": return ["Plan", "Amount", "ROI", "Duration", "Status", "Started"];
      case "others": return ["Amount", "Type", "Description", "Date"];
      default: return [];
    }
  };

  const renderTableRow = (item, tab) => {
    switch (tab) {
      case "deposits":
        return (
          <>
            <td className="fw-bold" style={{ color: "#1a1a1a" }}>${Number(item.amount || 0).toFixed(2)}</td>
            <td><span className="badge bg-dark text-white border-0">{item.method || "—"}</span></td>
            <td><StatusBadge status={item.status} /></td>
            <td className="fw-medium" style={{ color: "#4a4a4a" }}>{formatDate(item.timestamp || item.createdAt)}</td>
          </>
        );
      case "withdrawals":
        return (
          <>
            <td className="fw-bold" style={{ color: "#1a1a1a" }}>${Number(item.amount || 0).toFixed(2)}</td>
            <td className="fw-medium" style={{ color: "#4a4a4a" }}>${Number(item.fee || 0).toFixed(2)}</td>
            <td><span className="badge bg-dark text-white border-0">{item.cryptoType || item.paymentMethod || "—"}</span></td>
            <td><StatusBadge status={item.status} /></td>
            <td className="fw-medium" style={{ color: "#4a4a4a" }}>{formatDate(item.createdAt)}</td>
          </>
        );
      case "investments":
        return (
          <>
            <td className="fw-bold" style={{ color: "#1a1a1a" }}>{item.plan || "Standard Plan"}</td>
            <td className="fw-bold" style={{ color: "#1a1a1a" }}>${Number(item.amount || 0).toFixed(2)}</td>
            <td><span className="badge bg-success bg-opacity-10 text-success border border-success">{item.roi}%</span></td>
            <td className="fw-medium" style={{ color: "#4a4a4a" }}>{item.duration} days</td>
            <td><StatusBadge status={item.status} /></td>
            <td className="fw-medium" style={{ color: "#4a4a4a" }}>{formatDate(item.createdAt)}</td>
          </>
        );
      case "others":
        return (
          <>
            <td className="fw-bold" style={{ color: "#1a1a1a" }}>${Number(item.amount || 0).toFixed(2)}</td>
            <td><span className="badge bg-info bg-opacity-10 text-info border border-info">{item.type || "Bonus"}</span></td>
            <td className="fw-medium" style={{ color: "#4a4a4a" }}>{item.narration || item.description || "—"}</td>
            <td className="fw-medium" style={{ color: "#4a4a4a" }}>{formatDate(item.createdAt)}</td>
          </>
        );
      default:
        return null;
    }
  };

  const filteredData = filterTransactions(transactions[activeTab] || []);
  const tableHeaders = getTableHeaders(activeTab);

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>APEXYEILD | Transaction History</title>
        <link rel="icon" href="https://apexyeild.com/auth/storage/app/public/photos/IQAXeGtikmark.png1744508352" type="image/png" />
        <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.2/css/all.min.css" />
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      </Helmet>
      <style>{`
        :root { --primary: #0b57e3; --muted: #64748b; --border: #e2e8f0; --bg-light: #f8fafc; }
        body { font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; background: var(--bg-light); }
        .hover-bg-light:hover { background: #f1f5f9 !important; }
        .nav-tabs { border-bottom: 1px solid var(--border); gap: 0.25rem; }
        .nav-tabs .nav-link { border: none; border-radius: 10px 10px 0 0; padding: 0.85rem 1.5rem; font-weight: 500; color: var(--muted); background: transparent; transition: all 0.2s ease; display: flex; align-items: center; gap: 0.5rem; }
        .nav-tabs .nav-link:hover { color: var(--primary); background: rgba(11, 87, 227, 0.08); }
        .nav-tabs .nav-link.active { color: var(--primary); background: #fff; border-bottom: 2px solid var(--primary); font-weight: 600; }
        .nav-tabs .nav-link .badge { font-size: 0.7rem; padding: 0.2rem 0.5rem; margin-left: 0.25rem; }
        .table { --bs-table-bg: transparent; margin-bottom: 0; }
        .table th { font-size: 0.75rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); border-bottom: 1px solid var(--border); padding: 1rem 0.75rem; white-space: nowrap; }
        .table td { padding: 1rem 0.75rem; vertical-align: middle; font-size: 0.9rem; }
        .stat-card { border-radius: 16px; border: 1px solid var(--border); background: #fff; transition: transform 0.2s ease, box-shadow 0.2s ease; }
        .stat-card:hover { transform: translateY(-2px); box-shadow: 0 8px 25px rgba(0,0,0,0.08); }
        .stat-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 1.25rem; }
        .search-input { border-radius: 10px; border: 1px solid var(--border); padding: 0.65rem 1rem 0.65rem 2.5rem; font-size: 0.9rem; background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%2364748b'%3e%3cpath fill-rule='evenodd' d='M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z' clip-rule='evenodd'/%3e%3c/svg%3e"); background-repeat: no-repeat; background-position: 0.75rem center; background-size: 1rem; }
        .filter-select { border-radius: 10px; border: 1px solid var(--border); padding: 0.65rem 2rem 0.65rem 1rem; font-size: 0.9rem; appearance: none; }
        @media (max-width: 768px) { .nav-tabs { flex-wrap: wrap; justify-content: center; } .table-responsive { font-size: 0.85rem; } }
      `}</style>
      <ToastContainer position="top-right" autoClose={2500} hideProgressBar newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />
      <div className="wrapper">
        <Nav />
        <div className="main-panel bg-light">
          <div className="content bg-light">
            <div className="page-inner py-4">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 pb-2 border-bottom">
                <div>
                  <h2 className="fw-bold text-dark mb-1">Transaction History</h2>
                  <p className="text-muted mb-0">View and manage all your account activity</p>
                </div>
                <div className="mt-3 mt-md-0 d-flex gap-2">
                  <button className="btn btn-outline-primary btn-sm" onClick={() => exportToCSV(transactions[activeTab], activeTab)}>
                    <Icon name="download" className="me-1" /> Export CSV
                  </button>
                </div>
              </div>

              <div className="row g-3 mb-4">
                <div className="col-6 col-lg-3">
                  <div className="stat-card p-3">
                    <div className="d-flex align-items-center gap-3">
                      <div className="stat-icon" style={{ background: "rgba(16, 185, 129, 0.15)", color: "#10b981" }}><Icon name="deposit" /></div>
                      <div><p className="text-muted small mb-0">Total Deposits</p><h5 className="fw-bold mb-0">${Number(stats.totalDeposits).toFixed(2)}</h5></div>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-lg-3">
                  <div className="stat-card p-3">
                    <div className="d-flex align-items-center gap-3">
                      <div className="stat-icon" style={{ background: "rgba(239, 68, 68, 0.15)", color: "#ef4444" }}><Icon name="withdrawal" /></div>
                      <div><p className="text-muted small mb-0">Total Withdrawn</p><h5 className="fw-bold mb-0">${Number(stats.totalWithdrawals).toFixed(2)}</h5></div>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-lg-3">
                  <div className="stat-card p-3">
                    <div className="d-flex align-items-center gap-3">
                      <div className="stat-icon" style={{ background: "rgba(59, 130, 246, 0.15)", color: "#3b82f6" }}><Icon name="investment" /></div>
                      <div><p className="text-muted small mb-0">Active Investments</p><h5 className="fw-bold mb-0">{stats.activeInvestments}</h5></div>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-lg-3">
                  <div className="stat-card p-3">
                    <div className="d-flex align-items-center gap-3">
                      <div className="stat-icon" style={{ background: "rgba(11, 87, 227, 0.15)", color: "#0b57e3" }}><Icon name="wallet" /></div>
                      <div><p className="text-muted small mb-0">Available Balance</p><h5 className="fw-bold mb-0">${Number(stats.netBalance).toFixed(2)}</h5></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "16px" }}>
                <div className="card-body p-3 p-md-4">
                  <div className="row g-3 align-items-center">
                    <div className="col-md-6 col-lg-4">
                      <input type="text" className="form-control search-input w-100" placeholder="Search transactions..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                    </div>
                    <div className="col-md-6 col-lg-3">
                      <select className="form-select filter-select w-100" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
                        <option value="all">All Time</option>
                        <option value="7d">Last 7 Days</option>
                        <option value="30d">Last 30 Days</option>
                        <option value="90d">Last 90 Days</option>
                      </select>
                    </div>
                    <div className="col-md-12 col-lg-5 text-lg-end">
                      <span className="text-muted small">Showing {filteredData.length} of {transactions[activeTab]?.length || 0} transactions</span>
                    </div>
                  </div>
                </div>
              </div>

              <ul className="nav nav-tabs mb-0" role="tablist">
                {tabs.map((tab) => (
                  <li className="nav-item" role="presentation" key={tab.id}>
                    <button className={`nav-link ${activeTab === tab.id ? "active" : ""}`} onClick={() => setActiveTab(tab.id)} role="tab">
                      <Icon name={tab.icon} className="small" />
                      {tab.label}
                      {tab.count > 0 && <span className="badge bg-primary bg-opacity-10 text-primary rounded-pill">{tab.count}</span>}
                    </button>
                  </li>
                ))}
              </ul>

              <div className="card border-0 shadow-sm" style={{ borderRadius: "0 0 16px 16px", borderTop: "none" }}>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="bg-light">
                        <tr>{tableHeaders.map((header, idx) => (<th key={idx} className={idx === 0 ? "ps-4" : ""}>{header}</th>))}</tr>
                      </thead>
                      <tbody>
                        {filteredData.length === 0 ? (
                          <tr>
                            <td colSpan={tableHeaders.length} className="py-5">
                              <EmptyState
                                icon={activeTab === "deposits" ? "deposit" : activeTab === "withdrawals" ? "withdrawal" : activeTab === "investments" ? "investment" : "bonus"}
                                title={`No ${activeTab} found`}
                                description={searchQuery ? "Try adjusting your search or filters" : `You haven't made any ${activeTab} yet`}
                                action={
                                  activeTab === "deposits" && (
                                    <a href="/deposit" className="btn btn-primary btn-sm">
                                      <Icon name="deposit" className="me-1" /> Make a Deposit
                                    </a>
                                  )
                                }
                              />
                            </td>
                          </tr>
                        ) : (
                          filteredData.map((item) => <TableRow key={item.id}>{renderTableRow(item, activeTab)}</TableRow>)
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>
      
      <div className="modal fade" id="submitmt4modal" tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header border-0 pb-0">
              <div>
                <h5 className="modal-title fw-semibold">Subscribe to MT4 Trading</h5>
                <p className="text-muted small mb-0">Connect your MetaTrader 4 account</p>
              </div>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body pt-0">
              <form onSubmit={handleMt4Submit}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label small fw-medium">Duration</label>
                    <select className="form-select" name="duration" value={mt4Form.duration} onChange={handleInputChange} required>
                      <option value="default">Select plan</option>
                      <option value="Monthly">Monthly ($100)</option>
                      <option value="Quarterly">Quarterly ($250)</option>
                      <option value="Yearly">Yearly ($900)</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-medium">Amount</label>
                    <input className="form-control bg-light" type="text" value={calcAmount(mt4Form.duration) ? `$${calcAmount(mt4Form.duration)}` : "—"} disabled readOnly />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-medium">MT4 Login ID</label>
                    <input className="form-control" type="text" name="userid" value={mt4Form.userid} onChange={handleInputChange} placeholder="e.g. 12345678" required pattern="\d+" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-medium">MT4 Password</label>
                    <input className="form-control" type="password" name="pswrd" value={mt4Form.pswrd} onChange={handleInputChange} placeholder="••••••••" required />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-medium">Account Type</label>
                    <input className="form-control" type="text" name="acntype" value={mt4Form.acntype} onChange={handleInputChange} placeholder="e.g. Standard" required />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-medium">Currency</label>
                    <input className="form-control" type="text" name="currency" value={mt4Form.currency} onChange={handleInputChange} placeholder="e.g. USD" required />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-medium">Leverage</label>
                    <input className="form-control" type="text" name="leverage" value={mt4Form.leverage} onChange={handleInputChange} placeholder="e.g. 1:500" required />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-medium">Server</label>
                    <input className="form-control" type="text" name="server" value={mt4Form.server} onChange={handleInputChange} placeholder="e.g. APEX-Live" required />
                  </div>
                </div>
                <button type="submit" className="btn btn-primary w-100 py-3 mt-3" disabled={submitting || !mt4Form.userid || mt4Form.duration === "default"}>
                  {submitting ? "Processing..." : `Subscribe Now — $${calcAmount(mt4Form.duration) || "0"}`}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
export default UserHistory;